import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './Game2048.css';

const Game2048 = () => {
  const [board, setBoard] = useState(() => initializeBoard());
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [won, setWon] = useState(false);

  function initializeBoard() {
    const newBoard = Array(4).fill().map(() => Array(4).fill(0));
    addRandomTile(newBoard);
    addRandomTile(newBoard);
    return newBoard;
  }

  function addRandomTile(board) {
    const emptyCells = [];
    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 4; j++) {
        if (board[i][j] === 0) {
          emptyCells.push([i, j]);
        }
      }
    }
    if (emptyCells.length > 0) {
      const [row, col] = emptyCells[Math.floor(Math.random() * emptyCells.length)];
      board[row][col] = Math.random() < 0.9 ? 2 : 4;
    }
  }

  const moveLeft = useCallback((board) => {
    const newBoard = board.map(row => [...row]);
    let moved = false;
    let newScore = 0;

    for (let i = 0; i < 4; i++) {
      const row = newBoard[i].filter(cell => cell !== 0);
      for (let j = 0; j < row.length - 1; j++) {
        if (row[j] === row[j + 1]) {
          row[j] *= 2;
          newScore += row[j];
          if (row[j] === 2048 && !won) setWon(true);
          row[j + 1] = 0;
        }
      }
      const filteredRow = row.filter(cell => cell !== 0);
      while (filteredRow.length < 4) {
        filteredRow.push(0);
      }
      
      for (let j = 0; j < 4; j++) {
        if (newBoard[i][j] !== filteredRow[j]) {
          moved = true;
        }
        newBoard[i][j] = filteredRow[j];
      }
    }

    return { board: newBoard, moved, score: newScore };
  }, [won]);

  const rotateBoard = (board) => {
    const newBoard = Array(4).fill().map(() => Array(4).fill(0));
    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 4; j++) {
        newBoard[j][3 - i] = board[i][j];
      }
    }
    return newBoard;
  };

  const move = useCallback((direction) => {
    if (gameOver) return;

    let currentBoard = board.map(row => [...row]);
    let result;

    switch (direction) {
      case 'left':
        result = moveLeft(currentBoard);
        break;
      case 'right':
        currentBoard = rotateBoard(rotateBoard(currentBoard));
        result = moveLeft(currentBoard);
        result.board = rotateBoard(rotateBoard(result.board));
        break;
      case 'up':
        currentBoard = rotateBoard(rotateBoard(rotateBoard(currentBoard)));
        result = moveLeft(currentBoard);
        result.board = rotateBoard(result.board);
        break;
      case 'down':
        currentBoard = rotateBoard(currentBoard);
        result = moveLeft(currentBoard);
        result.board = rotateBoard(rotateBoard(rotateBoard(result.board)));
        break;
      default:
        return;
    }

    if (result.moved) {
      addRandomTile(result.board);
      setBoard(result.board);
      setScore(prev => prev + result.score);
      
      if (isGameOver(result.board)) {
        setGameOver(true);
      }
    }
  }, [board, gameOver, moveLeft]);

  const isGameOver = (board) => {
    // Check for empty cells
    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 4; j++) {
        if (board[i][j] === 0) return false;
      }
    }

    // Check for possible merges
    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 4; j++) {
        const current = board[i][j];
        if (
          (i < 3 && board[i + 1][j] === current) ||
          (j < 3 && board[i][j + 1] === current)
        ) {
          return false;
        }
      }
    }
    return true;
  };

  const resetGame = () => {
    setBoard(initializeBoard());
    setScore(0);
    setGameOver(false);
    setWon(false);
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      switch (e.key.toLowerCase()) {
        case 'a':
          e.preventDefault();
          move('left');
          break;
        case 'd':
          e.preventDefault();
          move('right');
          break;
        case 'w':
          e.preventDefault();
          move('up');
          break;
        case 's':
          e.preventDefault();
          move('down');
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [move]);

  return (
    <div className="game-container">
      <div className="game-header">
        <h1 className="game-title-header">🔢 2048</h1>
        <div className="game-instructions">
          Use WASD keys or buttons to move tiles. Combine tiles with the same number to reach 2048!
        </div>
      </div>

      <div className="game-board">
        <div className="score-display">Score: {score}</div>
        
        {won && !gameOver && (
          <div className="game-message win-message">
            <h2>You Win! 🎉</h2>
            <button onClick={() => setWon(false)}>Continue Playing</button>
          </div>
        )}

        {gameOver && (
          <div className="game-message game-over-message">
            <h2>Game Over!</h2>
            <p>Final Score: {score}</p>
          </div>
        )}

        <div className="game-play-area">
          <div className="game2048-board">
            {board.map((row, i) =>
              row.map((cell, j) => (
                <div
                  key={`${i}-${j}`}
                  className={`game2048-cell ${cell ? `cell-${cell}` : ''}`}
                >
                  {cell || ''}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="control-buttons">
          <button onClick={() => move('up')}>W</button>
          <div>
            <button onClick={() => move('left')}>A</button>
            <button onClick={() => move('down')}>S</button>
            <button onClick={() => move('right')}>D</button>
          </div>
        </div>
      </div>

      <div className="game-controls">
        <button className="btn btn-primary" onClick={resetGame}>
          Reset Game
        </button>
        <Link to="/" className="btn btn-secondary">
          Back to Home
        </Link>
      </div>
    </div>
  );
};

export default Game2048;